<template>
  <div>
    <div class="d-flex justify-content-between">
      <h1 class="page-header">{{ $t('tradeIn.pageTitle') }}</h1>
      <ts-breadcrumb
        :items="[
          { text: $t('home'), href: '/' },
          {
            text: $t('tradeIn.pageTitle'),
            active: true
          }
        ]"
      />
    </div>
    <ts-card>
      <ts-card-wrapper>
        <div class="d-flex justify-content-between">
          <div class="ts-flex ts-items-center ts-space-x-3">
            <ts-button outline color="info" @click.prevent="addNew">
              {{ $t('add_new') }}
            </ts-button>
            <a-range-picker
              v-model="dateRange"
              :allowClear="false"
              format="DD-MM-YYYY"
              valueFormat="DD-MM-YYYY"
              class="ts-w-64"
              @change="fetchData()"
            />
          </div>
          <div class="d-flex ts-space-x-3 ts-items-center">
            <Tooltip
              :content="$t('tradeIn.search')"
              placement="top"
              transfer
              :max-width="width"
            >
              <Input
                search
                v-model="search"
                :placeholder="$t('tradeIn.search')"
                :style="width"
                @on-focus="() => (width = 'width: 500px')"
                @on-blur="() => (width = 'width: 300px')"
              />
            </Tooltip>
            <ts-filter ref="filter">
              <div class="row">
                <div class="col-md-12">
                  <label class="">{{ $t('tradeIn.branch') }}</label>
                  <ts-branch-filter-v2
                    @filter="branchFilter"
                    class="ts-w-full"
                  ></ts-branch-filter-v2>
                </div>
              </div>
              <div class="row ts-mt-4">
                <div class="col-md-12">
                  <label class="">{{ $t('tradeIn.warehourse') }}</label>
                  <ts-location-filter
                    @filter="filterLocaton"
                  ></ts-location-filter>
                </div>
              </div>
            </ts-filter>
          </div>
        </div>
      </ts-card-wrapper>
      <Table
        :columns="columns"
        :data="resources"
        size="small"
        :loading="loading"
        highlight-row
        stripe
        max-height="700"
      >
        <template slot-scope="{ row }" slot="branch">
          {{ row.branch ? row.branch.branch_name_en : 'N/A' }}
        </template>
        <template slot-scope="{ row }" slot="amount">
          <ts-display-currency
            :amount="row.invoice_amount"
            :currency_id="row.currency_id"
          />
        </template>
        <template slot-scope="{ row }" slot="amount_paid">
          <ts-display-currency
            :amount="row.amount_paid"
            :currency_id="row.currency_id"
          />
        </template>
        <template slot-scope="{ row }" slot="amount_due">
          <ts-display-currency
            :amount="row.amount_due"
            :currency_id="row.currency_id"
          />
        </template>
        <template slot-scope="{ row }" slot="payment_status">
          <span
            class="badge badge-success"
            v-if="row.is_paid && row.amount_due == 0"
            >Paid</span
          >
          <span
            class="badge badge-warning"
            v-else-if="!row.is_paid && row.amount_paid > 0"
            >Partial</span
          >
          <span class="badge badge-danger" v-else>Due</span>
        </template>
        <template slot-scope="{ row }" slot="customer">
          {{ row.customer ? row.customer.customer_name : 'N/A' }}
        </template>
        <template slot-scope="{ row }" slot="warehouse">
          {{ row.location ? row.location.location_name_en : 'N/A' }}
        </template>
        <template slot-scope="{ row }" slot="action">
          <a
            href="#"
            v-tooltip="$t('edit')"
            class="text-primary mr-2"
            @click.prevent="edit(row)"
          >
            <i class="fas fa-edit"></i>
          </a>
        </template>
      </Table>
      <div class="d-flex justify-content-end p-15">
        <ts-pagination v-model="pagination" @navigate="fetchData">
        </ts-pagination>
      </div>
    </ts-card>
  </div>
</template>

<script>
import { debounce } from 'lodash'
import { mapState } from 'vuex'
import expandRow from './table-expand.vue'

export default {
  name: 'tradeIn',
  data() {
    return {
      loading: false,
      width: 'width: 300px',
      vendor: []
    }
  },
  computed: {
    ...mapState('stock/tradeIn', ['resources', 'pagination', 'formViewDatas']),
    search: {
      get() {
        return this.$store.state.stock.tradeIn.search
      },
      set(newValue) {
        this.$store.commit('stock/tradeIn/SET_SEARCH', newValue)
        this.$store.commit('stock/tradeIn/RESET_CURRENT_PAGE')
      }
    },
    dateRange: {
      get() {
        return this.$store.state.stock.tradeIn.dateRange
      },
      set(newValue) {
        this.$store.commit('stock/tradeIn/SET_DATE_RANGE', newValue)
      }
    },
    default_precision() {
      return this.$store.state.authUser.sys_setting.decimal_digit
    },
    columns() {
      return [
        {
          type: 'expand',
          render: (h, params) => {
            return h(expandRow, {
              props: {
                row: params.row
              }
            })
          },
          width: 30
        },
        {
          title: this.$t('tradeIn.branch'),
          slot: 'branch',
          minWidth: 120,
          sortable: true
        },
        {
          title: this.$t('tradeIn.tradeInDate'),
          key: 'trade_in_date',
          minWidth: 150,
          align: 'center',
          sortable: true
        },
        {
          title: this.$t('tradeIn.tradeInNumber'),
          key: 'trade_in_number',
          minWidth: 150,
          sortable: true
        },
        {
          title: this.$t('tradeIn.customer'),
          slot: 'customer',
          minWidth: 140,
          sortable: true
        },
        {
          title: this.$t('tradeIn.referenceInvoice'),
          key: 'reference_no',
          minWidth: 160,
          sortable: true
        },
        {
          title: this.$t('tradeIn.amount'),
          slot: 'amount',
          minWidth: 140,
          align: 'right',
          sortable: true,
          tooltip: true
        },
        {
          title: this.$t('tradeIn.amountPaid'),
          slot: 'amount_paid',
          minWidth: 140,
          align: 'right',
          sortable: true,
          tooltip: true
        },
        {
          title: this.$t('tradeIn.amountDue'),
          slot: 'amount_due',
          minWidth: 140,
          align: 'right',
          sortable: true,
          tooltip: true
        },
        {
          title: this.$t('tradeIn.paymentStatus'),
          slot: 'payment_status',
          minWidth: 150,
          align: 'center',
          sortable: true
        },
        {
          title: this.$t('tradeIn.warehouse'),
          slot: 'warehouse',
          minWidth: 170,
          sortable: true
        },
        {
          title: this.$t('actions'),
          key: 'action',
          slot: 'action',
          fixed: 'right',
          align: 'center',
          minWidth: 90
        }
      ]
    }
  },
  methods: {
    addNew() {
      this.$router.push({ name: 'trade-in-create' })
    },
    fetchData(attributes) {
      this.loading = true
      this.$store
        .dispatch('stock/tradeIn/fetch', { ...attributes })
        .then(() => {
          this.loading = false
        })
        .catch(error => {
          this.loading = false
          this.$toasted.error(error.message)
        })
    },
    edit(row) {
      this.$store.commit('stock/tradeIn/SET_EDIT_DATA', row)
      this.$router.push({
        name: 'trade-in-edit',
        params: { id: row.trade_in_id }
      })
    },
    deleteRecord(row) {
      row._deleting = true
      this.$store
        .dispatch('stock/tradeIn/destroy', row.trade_in_id)
        .then(response => {
          this.$toasted.success(response.message)
          this.fetchData()
        })
        .catch(error => {
          this.$toasted.error(error.message)
        })
        .finally(() => {
          row._deleting = false
        })
    },
    branchFilter(value) {
      this.$store.commit('stock/tradeIn/SET_BRANCH_ID', value)
      this.fetchData()
    },
    filterLocaton(value) {
      this.$store.commit('stock/tradeIn/SET_LOCATION_ID', value)
      this.fetchData()
    }
  },
  watch: {
    search: debounce(function() {
      this.fetchData()
    }, 500)
  },
  beforeRouteEnter(to, from, next) {
    next(vm => {
      vm.fetchData()
    })
  },
  beforeRouteLeave(to, from, next) {
    this.$store.commit('stock/tradeIn/RESET_STATE')
    next()
  }
}
</script>
<style>
.ivu-input-with-suffix {
  height: 34px !important;
}
</style>
